import React, { useState, useEffect } from "react";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";

import { Panel, Chip, Eyebrow } from "./Panel";
import { clearNoteDraft } from "./NotesPanel";
import CircularLoader from "../../CircularLoader/CircularLoader";
import {
  INVOICE_STATUS_OPTIONS,
  isChecked,
  saveAdminInvoiceId,
  saveAdminInvoiceStatus,
  saveShipmentToggle,
  saveAttachments,
} from "../writes/workbenchWrites";
import { checkFileType } from "../../../helpers/helpers";
import noImage from "../../../assets/images/noImage.png";
import pdfIcon from "../../../assets/icons/pdfIcon.png";
import excelIcon from "../../../assets/icons/excelIcon.png";
import { useTranslation } from "react-i18next";

/**
 * Admin controls — the writable panel.
 *
 * Every save here sends exactly ONE key to manage-invoice, the way production
 * does across its five separate call sites. There is no batched save: that
 * payload shape has never reached the endpoint.
 *
 * Two status tracks are kept distinct. admin_invoice_status is writable;
 * user_invoice_status is displayed read-only. They are separate fields with
 * separate write paths, and production has a latent bug where the read ternary
 * includes "admin-user" but the write ternary does not. The "(By User)" Select
 * is only pointer-events-disabled, so it is still reachable by Tab + Space and
 * then POSTs admin_invoice_status while showing user_invoice_status. That
 * keyboard-only path is deliberately NOT reproduced. Do not make the customer
 * track writable here.
 *
 * Every handler below mirrors its AdminActions.jsx counterpart: same key, same
 * value, same optimistic/confirmed local update, same grid-row source (the
 * RESPONSE value where production reads the response), same snack text.
 * Deliberate departures, all local-state only and listed in COVERAGE.md:
 * setData is functional (production's stale-closure spread reverts edits made
 * while a request is in flight), and each control has its own busy flag and
 * is disabled while its own request is in flight (production shares one
 * `loading` slot, so starting one control re-enables another mid-request).
 */
const Toggle = ({ on, onChange, disabled }) => (
  <button
    type="button"
    onClick={onChange}
    disabled={disabled}
    className={`relative h-[18px] w-8 flex-none rounded-full transition-colors disabled:opacity-50 ${
      on ? "bg-state-success-solid" : "bg-ui-border-strong"
    }`}
  >
    <span
      className={`absolute top-[3px] h-3 w-3 rounded-full bg-ui-surface transition-all ${
        on ? "right-[3px]" : "left-[3px]"
      }`}
    />
  </button>
);

const iconFor = (file) => {
  const type = checkFileType(file);
  if (type === "PDF") return pdfIcon;
  if (type === "Excel") return excelIcon;
  return null;
};

const AdminControlsPanel = ({ data, setData, setShipments, setSnack }) => {
  const { t } = useTranslation("admin");
  // One flag per control, so overlapping saves cannot clear or re-enable each
  // other. Keys: "status", "adminId", "is_bill_verified", "is_duty_paid",
  // "attachment".
  const [busy, setBusyMap] = useState({});
  const isBusy = (key) => !!busy[key];
  const setBusy = (key, value) =>
    setBusyMap((prev) => ({ ...prev, [key]: value }));

  const [adminInvoiceId, setAdminInvoiceId] = useState("");
  const [isIdModified, setIsIdModified] = useState(false);

  // matches Classic AdminActions.jsx:382-409 (the seed at 405-407), see
  // COVERAGE.md. Runs on EVERY data change, not only when the id changes, and
  // only when the stored id is truthy: a status change, a toggle or a charge
  // save puts an unsaved typed id back to the stored one while Save stays
  // visible, and a falsy stored id leaves the typed text alone.
  useEffect(() => {
    if (data?.admin_invoice_id) {
      setAdminInvoiceId(data?.admin_invoice_id);
    }
  }, [data]);

  const snack = (text, severity) =>
    setSnack?.((prev) => ({ ...prev, open: true, text, severity }));

  // setShipments is optional: three Dispatcher call sites mount the invoice
  // without it, where production throws after a successful write.
  const patchRow = (patch) =>
    setShipments?.((prev) => ({
      ...prev,
      data: prev?.data?.map((shipment) =>
        shipment.id === data.id ? { ...shipment, ...patch } : shipment,
      ),
    }));

  const syncRow = (patch) => {
    setData((prev) => ({ ...prev, ...patch }));
    patchRow(patch);
  };

  /* Status — matches Classic AdminActions.jsx:286-353, see COVERAGE.md.
     Optimistic: the local invoice takes the picked value BEFORE the request
     and is NOT rolled back on error. The grid row takes the value the server
     echoes back (result.admin_invoice_status), not the value sent. */
  const handleStatus = async (event) => {
    const status = event.target.value;
    setBusy("status", true);
    setData((prev) => ({ ...prev, admin_invoice_status: status }));
    const response = await saveAdminInvoiceStatus(data?.id, status);
    setBusy("status", false);

    if (response?.error) {
      snack(t("common:messages.somethingWentWrong"), "error");
      return;
    }
    snack(t("invoice.statusUpdatedSuccessfully"), "success");
    patchRow({
      admin_invoice_status: response?.data?.result?.admin_invoice_status,
    });
  };

  /* Admin invoice ID gate — copied verbatim from Classic
     AdminActions.jsx:73-80, see COVERAGE.md. Computed on each keystroke
     against the stored value at that moment and held in state, so it is NOT
     recomputed when data changes. When the key is missing from the payload
     (undefined), every string, "" included, shows Save. */
  const handleAdminIdChange = (value) => {
    const newId = value;
    const isModified =
      (data?.admin_invoice_id !== null && newId !== data?.admin_invoice_id) ||
      (data?.admin_invoice_id === null && newId !== "");
    setAdminInvoiceId(newId);
    setIsIdModified(isModified);
  };

  /* matches Classic AdminActions.jsx:82-143, see COVERAGE.md. On success it
     also empties the unsaved "Add a note" draft WITHOUT hiding that Save
     (AdminActions.jsx:101), so a later note Save sends invoice_description="". */
  const handleAdminId = async () => {
    setBusy("adminId", true);
    const shipmentId = data?.id;
    const response = await saveAdminInvoiceId(shipmentId, adminInvoiceId);
    setBusy("adminId", false);

    if (response?.error) {
      snack(t("common:messages.somethingWentWrong"), "error");
      return;
    }
    snack(t("invoice.idUpdatedSuccessfully"), "success");
    setIsIdModified(false);
    clearNoteDraft(shipmentId);
    syncRow({
      admin_invoice_id: response?.data?.result?.admin_invoice_id,
    });
  };

  /* Toggles — matches Classic updateShipmentToggle, AdminActions.jsx:411-474,
     see COVERAGE.md. Optimistic update to 1/0 before the request; on error
     roll back to `checked ? 0 : 1` (so a raw null or "false" becomes a
     number); on success the grid row takes result[field] from the response
     and the local invoice keeps the optimistic value. */
  const handleToggle = async (field, checked) => {
    setBusy(field, true);
    const value = checked ? 1 : 0;
    setData((prev) => ({ ...prev, [field]: value }));
    const response = await saveShipmentToggle(data?.id, field, checked);
    setBusy(field, false);

    if (response?.error) {
      setData((prev) => ({ ...prev, [field]: checked ? 0 : 1 }));
      snack(t("common:messages.somethingWentWrong"), "error");
      return;
    }
    snack(t("invoice.statusUpdatedSuccessfully"), "success");
    patchRow({ [field]: response?.data?.result?.[field] });
  };

  /* Attachments — matches Classic handleAdminAttachmentChange,
     AdminActions.jsx:214-285, see COVERAGE.md. Production's FileList.forEach
     does NOT throw: html2pdf.js (imported by InvoiceModal.jsx:18) bundles
     core-js's web.dom-collections.for-each, which installs forEach on
     FileList.prototype. So the request goes out; Array.from here yields the
     same files in the same order. The input's value is never cleared, after
     success or failure, exactly like production, so re-picking the identical
     selection fires no change event and sends nothing. */
  const handleAttachments = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setBusy("attachment", true);
    const response = await saveAttachments(data?.id, Array.from(files));
    setBusy("attachment", false);

    if (response?.error) {
      snack(t("common:messages.somethingWentWrong"), "error");
      return;
    }
    snack(t("invoice.attachmentAddedSuccessfully"), "success");
    // Production writes result.media to the invoice AND the grid row
    // (AdminActions.jsx:244-262) unconditionally — a response without `media`
    // blanks both, and that data change also resets the charge editor's
    // invented lines. Matched as-is, so the next charge write is the same too.
    syncRow({ media: response?.data?.result?.media });
  };

  const adminMedia = (data?.media || []).filter((m) => m?.meta === "admin");

  return (
    <Panel
      title={t("invoice.adminControls")}
      chip={t("invoice.byAdmin")}
      chipTone="info"
      hint={t("invoice.hintClient", { name: data?.user?.username ?? "—" })}
    >
      <div className="flex flex-col gap-2.5 px-2.5 py-2">
        {/* Invoice status — admin track, writable */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-[124px] flex-none font-manrope text-[11.5px] font-semibold text-ui-text-secondary">
            {t("invoice.invoiceStatus")}
          </span>
          {/* The same MUI Select as production (AdminActions.jsx:510-543), not
              a native <select>: on a focused native select a letter or arrow
              key changes the value and would POST a status nobody picked, and
              an out-of-list stored value would display as "Unpaid". MUI only
              opens its menu on a keypress and shows such a value as blank. */}
          <FormControl
            className="min-w-[160px] flex-1"
            variant="outlined"
            size="small"
          >
            <Select
              value={data?.admin_invoice_status ?? "none"}
              onChange={handleStatus}
              disabled={isBusy("status")}
              sx={{ fontFamily: "Manrope, sans-serif", fontSize: 12 }}
            >
              <MenuItem disabled value="none">
                <em>{t("invoice.invoiceStatusPlaceholder")}</em>
              </MenuItem>
              {INVOICE_STATUS_OPTIONS.map((status) => (
                <MenuItem key={status} value={status}>
                  {t(`invoiceStatus.${status}`, { defaultValue: status })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isBusy("status") && <CircularLoader size={16} />}
        </div>

        {/* Customer-facing track — read-only on purpose */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-[124px] flex-none font-manrope text-[11.5px] font-semibold text-ui-text-secondary">
            {t("invoice.customerStatus")}
          </span>
          <span className="font-manrope text-[11px] font-medium text-ui-text">
            {data?.user_invoice_status
              ? t(`invoiceStatus.${data.user_invoice_status}`, {
                  defaultValue: data.user_invoice_status,
                })
              : "—"}
          </span>
          <Chip tone="neutral">{t("invoice.setByTheCustomer")}</Chip>
        </div>

        {/* Admin invoice ID */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-[124px] flex-none font-manrope text-[11.5px] font-semibold text-ui-text-secondary">
            {t("invoice.adminInvoiceId")}
          </span>
          <input
            value={adminInvoiceId}
            onChange={(e) => handleAdminIdChange(e.target.value)}
            placeholder={t("invoice.enterAdminInvoiceId")}
            className="min-w-[120px] flex-1 rounded-[5px] border border-ui-border-strong bg-ui-surface px-2 py-1.5 font-manrope text-[12px] font-medium tabular-nums text-ui-text"
          />
          {isIdModified && (
            <button
              onClick={handleAdminId}
              disabled={isBusy("adminId")}
              className="flex items-center gap-1.5 rounded-[5px] border border-state-success-solid bg-state-success-solid px-2.5 py-1.5 font-manrope text-[10.5px] font-semibold text-brand-contrast hover:bg-state-success-solid-hover disabled:opacity-50"
            >
              {isBusy("adminId") ? (
                <CircularLoader size={14} color="grey" />
              ) : (
                t("common:actions.save")
              )}
            </button>
          )}
        </div>

        {/* Billing verification */}
        <div className="flex items-center justify-between gap-2 rounded-[5px] border border-ui-border bg-ui-surface-sunken px-2 py-1.5">
          <span className="font-manrope text-[10.5px] font-semibold text-ui-text">
            {t("invoice.billingVerification")}{" "}
            <span className="font-medium text-ui-text-muted">
              {isChecked(data?.is_bill_verified) ? t("invoice.verifiedParen") : t("invoice.unverifiedParen")}
            </span>
          </span>
          <div className="flex items-center gap-2">
            {isBusy("is_bill_verified") && <CircularLoader size={14} />}
            <Toggle
              on={isChecked(data?.is_bill_verified)}
              disabled={isBusy("is_bill_verified")}
              onChange={() =>
                handleToggle(
                  "is_bill_verified",
                  !isChecked(data?.is_bill_verified),
                )
              }
            />
          </div>
        </div>

        {/* Duties & taxes — production renders this ONLY when a customs
            declaration file exists. Rendering it unconditionally would let
            admins write is_duty_paid on domestic shipments. */}
        {data?.single_shipping_invoice?.file && (
          <div className="flex items-center justify-between gap-2 rounded-[5px] border border-ui-border bg-ui-surface-sunken px-2 py-1.5">
            <span className="font-manrope text-[10.5px] font-semibold text-ui-text">
              {t("invoice.dutiesTaxes")}{" "}
              <span className="font-medium text-ui-text-muted">
                {isChecked(data?.is_duty_paid) ? t("invoice.paidParen") : t("invoice.unpaidParen")}
              </span>
            </span>
            <div className="flex items-center gap-2">
              {isBusy("is_duty_paid") && <CircularLoader size={14} />}
              <Toggle
                on={isChecked(data?.is_duty_paid)}
                disabled={isBusy("is_duty_paid")}
                onChange={() =>
                  handleToggle("is_duty_paid", !isChecked(data?.is_duty_paid))
                }
              />
            </div>
          </div>
        )}

        {/* Attachments — shipment media, meta === "admin" */}
        <div>
          <Eyebrow className="mb-1.5">{t("invoice.attachments")}</Eyebrow>
          <div className="flex flex-wrap items-center gap-1.5">
            {adminMedia.map((file) => {
              const icon = iconFor(file?.file);
              return (
                <button
                  key={file?.id}
                  onClick={() => window.open(file?.file, "_blank")}
                  className="flex items-center gap-1.5 rounded border border-ui-border bg-ui-surface-sunken px-1.5 py-1 font-manrope text-[10px] font-semibold text-ui-text-secondary hover:bg-ui-row-hover"
                >
                  <img
                    src={icon || file?.file || noImage}
                    alt=""
                    className="h-4 w-4 rounded-sm object-cover"
                  />
                  {file?.file_name || t("invoice.attachmentFallback")}
                </button>
              );
            })}

            <label className="flex cursor-pointer items-center gap-1.5 rounded border border-dashed border-ui-border-strong bg-ui-surface px-2 py-1 font-manrope text-[10px] font-semibold text-ui-text-muted hover:border-brand hover:text-brand">
              <input
                type="file"
                multiple
                hidden
                onChange={handleAttachments}
                disabled={isBusy("attachment")}
              />
              {isBusy("attachment") ? t("invoice.uploading") : t("invoice.addPlus")}
            </label>
          </div>
        </div>
      </div>
    </Panel>
  );
};

export default AdminControlsPanel;
